import {CaretRightOutlined, HourglassOutlined, ProfileTwoTone, SyncOutlined} from '@ant-design/icons';
import {Button, Checkbox, Col, Collapse, Drawer, PageHeader, Popover, Row, Table, Tabs, Tooltip,} from 'antd';
import _ from 'lodash';
import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {setCurrentQuery, setCurrentStudy} from '../CohortBuilder/cohortBuilderSlice';
import EditableLabel from '../../components/EditableLabel';
import {isDefined, isNotDefined, toURL} from '../../reducers/util';
import {CohortSummary} from '../CohortBuilder/CohortSummary';
import ListQueries from '../../components/cohort-queries/ListQueries';
import SparkQueryStatus from '../SparkQueryStatus';
import {FAILED_STATUS, FINISHED_STATUS} from '../CohortBuilder/cohortStatus';
import {
    loadReport,
    newReport,
    refreshSavedReports,
    saveAndThenRunReport,
    saveReport,
    setActiveGroup,
    setActivePeriod,
    setReportTitle,
    setShowDataSource
} from './actions/index';
import GroupTab from './GroupTab';
import IndexPeriodChooser from './IndexPeriodChooser';
import Metrics from './Metrics';
import {emptyGroup} from './reducers';
import ReportSummary from './ReportSummary';
import SavedReportMenu from './SavedReportMenu';
import {calculateHeaders, placeHolderData} from './util';

const { TabPane } = Tabs
const { Panel } = Collapse

const UPDATE_INTERVAL = 1000 * 60;

const HealthcareUsageReport = ({ report, activeGroup, activePeriod, activeTab, setCurrentStudyAndQuery, newReport, saveReport, loadReport, refreshSavedReports, saveAndThenRunReport, setShowDataSource, setReportTitle, setActiveGroup, setActivePeriod }) => {

    const hasGroupBeenAdded = (group) => {
        const array = report.reportConfig[`group${group.level}`]
        const index = _.findIndex(array, { id: group.id })
        if (index > -1) {
            return true
        } else {
            return false
        }
    }

    const amendingGroup = () => {
        return hasGroupBeenAdded(activeGroup) ? activeGroup : null
    }

    const [drawerVisible, setDrawerVisible] = useState(false)
    const [headers, setHeaders] = useState(calculateHeaders(report.reportConfig, setActivePeriod, setActiveGroup, amendingGroup()))
    const [data, setData] = useState(null)

    // needed for the summary
    const [cohortRequest, setCohortRequest] = useState(null)
    // needed for the summary
    const [cohortResponse, setCohortResponse] = useState(null)

    const fetchQuery = () => {
        const url = toURL('/query?queryId=' + report.queryId)
        fetch(url, {
            credentials: process.env.NODE_ENV === "development" ? "include" : "same-origin"
        })
            .then(response => response.json())
            .then(query => {
                const request = JSON.parse(query.sparkQuery.request).request
                setCohortRequest(request)

                const response = JSON.parse(query.sparkQuery.response)
                setCohortResponse(response)
            })
            .catch(() => console.log('some error happened while fetching the cohort'))
    }

    const fetchStudy = () => {
        const url = toURL('/study?queryId=' + report.queryId)
        fetch(url, {
            credentials: process.env.NODE_ENV === "development" ? "include" : "same-origin"
        })
            .then(response => response.json())
            .then(study => {
                setCurrentStudyAndQuery(study, report.queryId)
            })
            .catch(() => console.log('some error happened while fetching the queries'))
    }

    useEffect(() => {
        fetchQuery()
        fetchStudy()
        const headers = calculateHeaders(report.reportConfig, setActivePeriod, setActiveGroup, amendingGroup())
        setHeaders(headers)
        setData(placeHolderData(report))

        if (queryHasBeenRunAndIsNotComplete(report.sparkQuery)) {
            let interval = null
            interval = setInterval(() => {
                loadReport(report.id)
                refreshSavedReports(report.queryId)
            }, UPDATE_INTERVAL)
            return () => clearInterval(interval);
        }
    },
        // TODO investigate effect dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [report.queryId, report.reportConfig, activeGroup, activePeriod]
    )

    const tabClicked = (tabKey) => {
        const newActiveGroup = emptyGroup(tabKey)
        setActiveGroup(newActiveGroup)
    }

    const tabClassName = hasGroupBeenAdded(activeGroup) ? "editing" : ""

    const tables = []

    const tableTemplate = (clazz, dataType, description) => {
        return {
            title: <div className="metrics-header">
                <span className={"data-source-header-" + clazz.toLowerCase()}>{dataType.toUpperCase()}</span> {description}
            </div>,
            element: <div key={clazz} className="data-source-metrics-container">
                <Table
                    columns={headers}
                    dataSource={isDefined(data) ? data[clazz.toLowerCase()] : null}
                    bordered
                    size="small"
                    scroll={{ x: 600, y: 600 }}
                    pagination={false}
                    rowKey={(row) => row.index}
                />
            </div>
        }
    }

    if (report.reportConfig.ip) {
        tables.push(
            tableTemplate('ip', 'ip', 'Inpatient Metrics')
        )
    }
    if (report.reportConfig.op) {
        tables.push(
            tableTemplate('op', 'op', 'Outpatient Metrics')
        )
    }

    if (report.reportConfig.aande) {
        tables.push(
            tableTemplate('aande', 'A&E', 'Accident & Emergency Metrics')
        )
    }

    const cohortSummary = () => {
        if (isDefined(cohortRequest) && isDefined(cohortResponse)) {
            return <CohortSummary label={cohortRequest.label} period={cohortRequest.period} groups={cohortRequest.groups} count={cohortResponse.count} tracker={cohortResponse.tracker} />
        } else {
            return <div><SyncOutlined spin /></div>;
        }
    }

    const reportConfig = () => {
        const content = <div className='config-options'>
            <Row>
                <Col md={24} lg={10} xxl={8}>
                    <div className="container">
                        <div className="data-source">
                            Data Source<br />
                            <Checkbox className="ip" checked={report.reportConfig.ip} onChange={e => setShowDataSource('ip', e.target.checked)}>IP</Checkbox><br />
                            <Checkbox className="op" checked={report.reportConfig.op} onChange={e => setShowDataSource('op', e.target.checked)}>OP</Checkbox><br />
                            <Checkbox className="ae" checked={report.reportConfig.aande} onChange={e => setShowDataSource('aande', e.target.checked)}>A&E</Checkbox><br />
                        </div>
                        <IndexPeriodChooser />
                        <Metrics />
                    </div>
                </Col>
                <Col md={24} lg={14} xxl={16}>
                    <div className="groups">
                        <Tabs type="card" activeKey={activeTab} onTabClick={e => tabClicked(e)} className={tabClassName}>
                            <TabPane tab="Group 1" key="1">
                                <GroupTab hasNotBeenSaved={!hasGroupBeenAdded(activeGroup)} />
                            </TabPane>
                            <TabPane tab="Group 2" key="2" disabled={report.reportConfig.group1.length === 0} className={tabClassName} >
                                <GroupTab hasNotBeenSaved={!hasGroupBeenAdded(activeGroup)} />
                            </TabPane>
                            <TabPane tab="Group 3" key="3" disabled={report.reportConfig.group2.length === 0} className={tabClassName}>
                                <GroupTab hasNotBeenSaved={!hasGroupBeenAdded(activeGroup)} />
                            </TabPane>
                        </Tabs>
                    </div>
                </Col>
            </Row>
        </div>

        return spinContent(report.sparkQuery, content)
    }

    const reportConfigOrReportSummary = () => {
        if (isDefined(report.sparkQuery)) {
            return <ReportSummary reportConfig={report.reportConfig} />
        } else {
            return reportConfig()
        }
    }

    const reportTable = () => {
        const content = <Collapse
            bordered={false}
            defaultActiveKey={['0', '1', '2']}
            expandIcon={({ isActive }) => <CaretRightOutlined rotate={isActive ? 90 : 0} />}
        >
            {
                tables
                    .map((elem, index) =>
                        <Panel header={elem.title} key={index}>
                            {elem.element}
                        </Panel>
                    )
            }
        </Collapse>

        return spinContent(report.sparkQuery, content)
    }

    const queryHasBeenRunAndIsNotComplete = (sparkQuery) => {
        return queryHasBeenRun(sparkQuery) &&
            (isNotDefined(sparkQuery.status) || (isDefined(sparkQuery.status) && (sparkQuery.status.toLowerCase() !== FINISHED_STATUS && sparkQuery.status.toLowerCase() !== FAILED_STATUS)))
    }

    const queryHasBeenRun = (sparkQuery) => {
        return isDefined(sparkQuery)
    }

    const queryHasFinished = (sparkQuery) => {
        return isDefined(sparkQuery) && isDefined(sparkQuery.status) && sparkQuery.status.toLowerCase() === FINISHED_STATUS
    }

    const spinContent = (sparkQuery, content) => {
        if (queryHasBeenRun(sparkQuery)) {
            if (queryHasFinished(sparkQuery)) {
                return content
            }
            // when submitted but status has not been set
            const _default = <Tooltip title="Waiting...">
                <HourglassOutlined />
            </Tooltip>

            return <SparkQueryStatus type="report" status={sparkQuery.status} whenStatusIsNull={_default} />
        } else {
            return content
        }
    }

    const reportHeader = () => {
        const editable = !isDefined(report.sparkQuery)
        const canExport = queryHasFinished(report.sparkQuery)
        const savedOnce = report.id > 0
        const reportTitle = editable ? <EditableLabel className="group-title-input" text={report.title} onSaveNotification={value => setReportTitle(value)} /> : report.title
        const content = <PageHeader
            style={{
                border: '1px solid rgb(235, 237, 240)',
            }}
            // onBack={() => null}
            title="Healthcare Resource Utilisation"
            subTitle={
                <span>for {isDefined(cohortRequest) ? cohortRequest.label : <SyncOutlined spin />} - {reportTitle}&nbsp;&nbsp;
                    <Popover content={cohortSummary()} title={"Cohort Summary"} trigger="click">
                        <ProfileTwoTone />
                    </Popover>
                </span>
            }
            // tags={<Tag color="blue">Running</Tag>}
            extra={[
                <Button key="0" size="small" onClick={() => { setDrawerVisible(true) }}>Cohorts</Button>,
                <Button key="1" type="primary" size="small" onClick={(e) => { newReport(report.queryId) }}>New Report</Button>,
                <Button key="2" type="primary" disabled={!editable || !savedOnce} size="small" onClick={(e) => { saveAndThenRunReport(report) }}>Run</Button>,
                <Button key="3" size="small" disabled={!editable} onClick={(e) => { saveReport(report, report.queryId) }}>Save</Button>,
                <SavedReportMenu key="4" />,
                <Button key="5" disabled={!canExport} size="small" onClick={(e) => { window.open(toURL("/downloadHRUExcel?reportId=" + report.id), "_blank") }}>Export</Button>,
            ]}
        />
        return content
    }

    return <div className="healthcare-resource-usage-report">
        <Row>
            <Col span={24}>
                {reportHeader()}
            </Col>
        </Row>
        <Row>
            <Col span={24}>
                {reportConfigOrReportSummary()}
            </Col>
        </Row>
        <Row>
            <Col span={24}>
                {reportTable()}
            </Col>
        </Row>
        <Drawer
            title="Cohorts"
            placement="right"
            closable={false}
            width="500"
            onClose={() => setDrawerVisible(false)}
            visible={drawerVisible}
            headerStyle={{ display: 'none' }}
        >
            <ListQueries />
        </Drawer>
    </div >
}

const mapStateToProps = (state, ownProps) => {
    return {
        report: state.hru.report,
        activeTab: state.hru.active.tab,
        activeGroup: state.hru.active.group,
        activePeriod: state.hru.active.period
    }
}

const mapDispatchToProps = dispatch => ({
    setCurrentStudyAndQuery: (study, queryId) => {
        dispatch(setCurrentStudy(study))
        dispatch(setCurrentQuery(queryId))
    },
    newReport: (queryId) => dispatch(newReport(queryId)),
    saveReport: (report, queryId) => dispatch(saveReport(report, queryId)),
    loadReport: (reportId) => dispatch(loadReport(reportId)),
    refreshSavedReports: (queryId) => dispatch(refreshSavedReports(queryId)),
    saveAndThenRunReport: (report) => dispatch(saveAndThenRunReport(report)),
    setShowDataSource: (dataSource, checked) => dispatch(setShowDataSource(dataSource, checked)),
    setReportTitle: (title) => dispatch(setReportTitle(title)),
    setActiveGroup: (group) => dispatch(setActiveGroup(group)),
    setActivePeriod: (period) => dispatch(setActivePeriod(period)),
})

export default connect(mapStateToProps, mapDispatchToProps)(HealthcareUsageReport)